import React, { useEffect, useMemo } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { ROLES_SIXT, useAuth } from 'auth'
import { useTranslation } from 'react-i18next'

import PageLoading from 'components/atoms/PageLoading'
import NotFound from 'components/atoms/NotFound'
import CarsBookedList from 'components/organisms/CarsBooked'
import useCarsBooked from 'components/organisms/CarsBooked/useCarsBooked'
import { getCarsBookedUrl } from 'components/organisms/CarsBooked/carsBookedLib'
import PageTitle from 'components/atoms/PageTitle'

import useSessionsList from 'hooks/useSessionsList'
import useSession from 'hooks/useSession'
import useEntityData from 'hooks/useEntityData'

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function CarsBookedRouter() {
  const { t } = useTranslation()

  const { getPartnerId, hasOneOfRoles } = useAuth()

  const admin = useMemo(() => hasOneOfRoles(ROLES_SIXT), [hasOneOfRoles])

  const { id, partnerId } = useParams()

  const {
    isFetching: isFetchingSessions,
    fetchSessions,
    sessions,
  } = useSessionsList()

  const {
    isFetching: isFetchingSession,
    fetchSession,
    session,
    error,
  } = useSession('selected')

  const {
    isFetching: isFetchingCars,
    fetchCarsBooked,
    cars: allCars,
    error: errorCarsBooked,
  } = useCarsBooked()

  const {
    isFetching: isFetchingCurrentSession,
    fetchCurrentCarsBookedSession,
    session: currentActiveSession,
  } = useSession('current')

  const {
    items: partners,
    isFetching: isFetchingPartners,
    refetch: fetchPartners,
  } = useEntityData({ limit: 9999 })

  useEffect(() => {
    fetchCurrentCarsBookedSession()
  }, [fetchCurrentCarsBookedSession])

  useEffect(() => {
    if (!admin) {
      return
    }

    fetchPartners(`partners/`)
  }, [admin, fetchPartners])

  useEffect(() => {
    if (!id || !partnerId) {
      return
    }
    fetchSessions()
  }, [fetchSessions, id, partnerId])

  useEffect(() => {
    if (!id || !partnerId) {
      return
    }
    fetchSession(id)
  }, [fetchSession, id, partnerId])

  const myPartnerId = useMemo(
    () =>
      admin
        ? partnerId &&
          partners.find((partner) => partner.partnerId === partnerId)
          ? partnerId
          : partners?.[0]?.partnerId
        : getPartnerId(),
    [admin, getPartnerId, partnerId, partners]
  )

  useEffect(() => {
    if (!session?.sessionId || !myPartnerId) {
      return
    }

    fetchCarsBooked(session?.sessionId, myPartnerId)
  }, [fetchCarsBooked, myPartnerId, session?.sessionId])

  const cars = useMemo(
    () =>
      allCars.filter((car) => car.choice || car.assignedState === 'assigned'),
    [allCars]
  )

  const readOnly = useMemo(
    () => isFetchingCars || isFetchingSessions || isFetchingSession,
    [isFetchingCars, isFetchingSessions, isFetchingSession]
  )

  const filteredSessions = useMemo(() => {
    if (
      !sessions.length ||
      !Object.keys(session).length ||
      !Object.keys(currentActiveSession).length
    ) {
      return sessions
    }

    let check = false

    return sessions
      .map((s) => {
        if (!check && s?.sessionId === currentActiveSession?.sessionId) {
          check = true
        }
        return check ? s : null
      })
      .filter((item) => item)
  }, [sessions, session, currentActiveSession])

  return (
    <div className="content-container">
      {isFetchingSessions ||
      isFetchingCurrentSession ||
      isFetchingPartners ||
      (isFetchingCars && !cars.length) ||
      isFetchingSession ||
      (id && partnerId && !Object.keys(session).length && !error) ? (
        <PageLoading />
      ) : !id || !partnerId ? (
        currentActiveSession?.sessionId && myPartnerId ? (
          <Navigate
            to={getCarsBookedUrl(
              id || currentActiveSession.sessionId,
              myPartnerId
            )}
          />
        ) : (
          <div data-testid="empty-content">
            <PageTitle label={t('assignment.title')} />
            {t('assignment.emptyContent')}
          </div>
        )
      ) : (error && Number(error.response.status) === 404) ||
        (errorCarsBooked && Number(errorCarsBooked.response.status) === 404) ? (
        <NotFound />
      ) : myPartnerId !== partnerId ? (
        <Navigate to={getCarsBookedUrl(session.sessionId, myPartnerId)} />
      ) : (
        <CarsBookedList
          session={session}
          partnerId={myPartnerId}
          partners={partners}
          cars={cars}
          sessions={filteredSessions}
          currentActiveSession={currentActiveSession}
          hasAccessToSession={!!allCars.length}
          readOnly={readOnly}
        />
      )}
    </div>
  )
}
